/**
 * 260428 오후회의2: 어떤 사진을 올려도 업로드 직전 클라이언트에서 리사이즈/압축.
 * - 가로/세로 max 한도 안에 들어오게 비율 유지 다운스케일
 * - JPG 압축 품질 조절
 * - createImageBitmap의 imageOrientation으로 EXIF 회전 자동 보정 (모던 모바일 브라우저)
 * - 이미지가 아니거나 이미 작으면 원본 그대로 반환
 *
 * 사용:
 *   const resized = await resizeImageFile(file, { maxWidth: 1600, maxHeight: 1200, quality: 0.82 });
 *   // 비율 강제 + letterbox (전체 다 보이게):
 *   const fitted = await resizeImageFile(file, { fitToAspect: { width: 1200, height: 900, background: '#0a0a0a' } });
 */
export async function resizeImageFile(file, options = {}) {
  if (!file || !(file instanceof Blob)) return file;
  if (!file.type || !file.type.startsWith('image/')) return file;
  // 이미 작은 GIF/SVG는 손대지 않음 (애니/벡터 파괴 방지)
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') return file;

  const {
    maxWidth = 1600,
    maxHeight = 1200,
    quality = 0.82,
    mimeType = 'image/jpeg',
    // 260918: true 면 한도 안 JPEG 도 캔버스로 다시 인코딩 — EXIF 회전을 픽셀에 굽고 메타를 벗긴다.
    //   BE(ImageIO) 는 EXIF 를 모르므로 원본 통과 시 썸네일/리사이즈본이 옆으로 눕는다(간판 사진 경로).
    forceReencode = false,
  } = options;

  // fitToAspect: { width, height, background } — 비율 유지 'contain' 모드 + 정확한 픽스 사이즈로 출력.
  // 원본 전체가 다 보이도록 letterbox 처리 (남는 영역은 background 색으로 채움).
  // 간판 이미지처럼 비율을 강제하되 "잘라내지 않고 전체를 보여주고 싶을 때" 사용.
  const { fitToAspect } = options;

  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    // createImageBitmap 미지원/실패 → Image 엘리먼트 fallback
    bitmap = await loadImageElement(file);
  }
  if (!bitmap) return file;

  const { width: srcW, height: srcH } = bitmap;
  if (!srcW || !srcH) return file;

  let dstW;
  let dstH;
  let drawArgs; // ctx.drawImage 인자

  if (fitToAspect && fitToAspect.width && fitToAspect.height) {
    // 강제 비율 + 정확 사이즈 (letterbox, 'contain' 모드)
    // 원본이 다 보이도록 비율 유지하며 축소, 남는 영역은 background로 채움.
    dstW = fitToAspect.width;
    dstH = fitToAspect.height;
    const targetRatio = dstW / dstH;
    const srcRatio = srcW / srcH;
    let drawW;
    let drawH;
    if (srcRatio > targetRatio) {
      // 원본이 더 가로로 길다 — 가로 기준 맞추고 세로 letterbox
      drawW = dstW;
      drawH = Math.round(dstW / srcRatio);
    } else {
      // 원본이 더 세로로 길다 — 세로 기준 맞추고 좌우 letterbox
      drawH = dstH;
      drawW = Math.round(dstH * srcRatio);
    }
    const dx = Math.round((dstW - drawW) / 2);
    const dy = Math.round((dstH - drawH) / 2);
    drawArgs = [bitmap, 0, 0, srcW, srcH, dx, dy, drawW, drawH];
  } else {
    // 기본: 비율 유지 다운스케일
    const ratio = Math.min(1, maxWidth / srcW, maxHeight / srcH);
    dstW = Math.round(srcW * ratio);
    dstH = Math.round(srcH * ratio);
    if (ratio === 1 && file.type === 'image/jpeg' && !forceReencode) {
      if (bitmap.close) bitmap.close();
      return file;
    }
    drawArgs = [bitmap, 0, 0, dstW, dstH];
  }

  const canvas = document.createElement('canvas');
  canvas.width = dstW;
  canvas.height = dstH;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  if (fitToAspect) {
    // letterbox 영역을 background 색으로 먼저 채우고 그 위에 이미지 그림
    ctx.fillStyle = fitToAspect.background || '#000000';
    ctx.fillRect(0, 0, dstW, dstH);
  }
  ctx.drawImage(...drawArgs);
  if (bitmap.close) bitmap.close();

  const outType = file.type === 'image/png' ? 'image/png' : mimeType;
  const blob = await canvasToBlob(canvas, outType, outType === 'image/png' ? undefined : quality);
  if (!blob) return file;

  // 결과가 원본보다 크면 원본 사용 (작은 이미지 + 강제 인코딩 시 역효과 방지)
  // fitToAspect 모드는 letterbox 캔버스 사이즈를 강제해야 하므로 항상 결과 사용. forceReencode 도 결과 사용(EXIF 제거가 목적).
  if (!fitToAspect && !forceReencode && blob.size >= file.size && dstW === srcW && dstH === srcH) return file;

  const baseName = (file.name || 'image').replace(/\.[^.]+$/, '');
  const ext = outType === 'image/png' ? 'png' : 'jpg';
  return new File([blob], `${baseName}.${ext}`, { type: outType, lastModified: Date.now() });
}

function canvasToBlob(canvas, mimeType, quality) {
  return new Promise((resolve) => {
    if (canvas.toBlob) {
      canvas.toBlob((b) => resolve(b), mimeType, quality);
    } else {
      // 구형 브라우저 fallback
      const dataUrl = canvas.toDataURL(mimeType, quality);
      resolve(dataUrlToBlob(dataUrl));
    }
  });
}

function dataUrlToBlob(dataUrl) {
  const [head, body] = dataUrl.split(',');
  const mime = head.match(/data:(.*?);/)?.[1] || 'image/jpeg';
  const bin = atob(body);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

function loadImageElement(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}
